/**
 * Decoding layer: turns any accepted upload (including HEIC/HEIF from
 * iPhones) into a canvas, honouring EXIF orientation.
 *
 * The original File object is never mutated or re-encoded — decoding
 * produces a separate working copy for the processing pipeline.
 *
 * Orientation is applied by us, not by the browser. iPhone camera captures
 * are always written as a landscape sensor frame plus an EXIF orientation
 * tag, and `createImageBitmap(blob, { imageOrientation: "from-image" })` is
 * unreliable inside a Web Worker on iOS Safari — the studio pipeline then
 * rectified and force-rotated a sideways frame, which is what produced the
 * corrupted hero renders for "Take Photo" uploads. Decoding with
 * `imageOrientation: "none"` and applying the EXIF transform ourselves makes
 * the result identical on every thread and every browser.
 */

import { ctx2d, makeCanvas, type AnyCanvas } from "./canvas";
import { MAX_INPUT_EDGE } from "./constants";

export function isHeic(file: { name: string; type: string }): boolean {
  const t = (file.type || "").toLowerCase();
  if (t === "image/heic" || t === "image/heif") return true;
  const n = file.name.toLowerCase();
  return n.endsWith(".heic") || n.endsWith(".heif");
}

/** Convert HEIC/HEIF to a browser-decodable JPEG blob. */
async function heicToJpeg(blob: Blob): Promise<Blob> {
  const { heicTo } = await import("heic-to");
  return (await heicTo({
    blob: blob as File,
    type: "image/jpeg",
    quality: 1,
  })) as Blob;
}

export type DecodedImage = {
  canvas: AnyCanvas;
  width: number;
  height: number;
  /** Source pixel dimensions before any safety downscale. */
  sourceWidth: number;
  sourceHeight: number;
  wasHeic: boolean;
  /** EXIF orientation found on the decoded bytes (1 when absent). */
  orientation: number;
};

/**
 * Read the EXIF orientation tag out of a JPEG. Returns 1 (no transform) for
 * every other container or when the tag is missing/unreadable.
 */
export async function readExifOrientation(blob: Blob): Promise<number> {
  try {
    const buf = await blob.slice(0, 256 * 1024).arrayBuffer();
    return locateOrientation(buf).value;
  } catch {
    return 1;
  }
}

/** Find the EXIF orientation value and the byte offset of its 16-bit value. */
function locateOrientation(buf: ArrayBuffer): { value: number; offset: number; little: boolean } {
  const none = { value: 1, offset: -1, little: false };
  try {
    const view = new DataView(buf);
    if (view.byteLength < 4 || view.getUint16(0) !== 0xffd8) return none;

    let offset = 2;
    while (offset + 4 <= view.byteLength) {
      const marker = view.getUint16(offset);
      if ((marker & 0xff00) !== 0xff00) break;
      if (marker === 0xffda || marker === 0xffd9) break;
      const size = view.getUint16(offset + 2);
      if (size < 2) break;

      if (marker === 0xffe1 && offset + 10 <= view.byteLength) {
        // "Exif\0\0" then a TIFF header.
        if (view.getUint32(offset + 4) !== 0x45786966) {
          offset += 2 + size;
          continue;
        }
        const tiff = offset + 10;
        if (tiff + 8 > view.byteLength) return none;
        const endian = view.getUint16(tiff);
        if (endian !== 0x4949 && endian !== 0x4d4d) return none;
        const little = endian === 0x4949;
        const ifd = tiff + view.getUint32(tiff + 4, little);
        if (ifd + 2 > view.byteLength) return none;
        const count = view.getUint16(ifd, little);
        for (let i = 0; i < count; i++) {
          const entry = ifd + 2 + i * 12;
          if (entry + 12 > view.byteLength) break;
          if (view.getUint16(entry, little) === 0x0112) {
            const value = view.getUint16(entry + 8, little);
            return value >= 1 && value <= 8 ? { value, offset: entry + 8, little } : none;
          }
        }
        return none;
      }
      offset += 2 + size;
    }
  } catch {
    /* orientation is an optimisation, never a gate */
  }
  return none;
}

/**
 * Return a copy of the JPEG whose EXIF orientation tag says 1. Browsers
 * disagree on whether `imageOrientation: "none"` is honoured (Safari can
 * still rotate), which rotated photos twice. Neutralising the tag makes every
 * decoder return raw sensor pixels, so our own transform is applied exactly
 * once. The seller's original File is never touched.
 */
export async function withNeutralOrientation(blob: Blob): Promise<Blob> {
  const head = await blob.slice(0, 256 * 1024).arrayBuffer();
  const found = locateOrientation(head);
  if (found.offset < 0 || found.value === 1) return blob;
  new DataView(head).setUint16(found.offset, 1, found.little);
  return new Blob([head, blob.slice(head.byteLength)], { type: blob.type || "image/jpeg" });
}

/** True when the orientation swaps the width and height axes. */
export function orientationSwapsAxes(orientation: number): boolean {
  return orientation >= 5 && orientation <= 8;
}

/**
 * Decode a file to a canvas. Images beyond MAX_INPUT_EDGE are rejected;
 * everything else is decoded at full resolution.
 */
export async function decodeImageFile(
  file: File,
  opts: { workingMaxEdge?: number } = {},
): Promise<DecodedImage> {
  const heic = isHeic(file);
  const blob: Blob = heic ? await heicToJpeg(file) : file;

  const exifOrientation = await readExifOrientation(blob);
  const source = await decodeBlob(await withNeutralOrientation(blob));
  // The <img> decoder applies EXIF natively; the bitmap decoder is forced to
  // "none" so we can apply it deterministically here. Never apply it twice.
  const drawOrientation = exifOrientation;
  const swap = orientationSwapsAxes(drawOrientation);

  const sourceWidth = swap ? source.height : source.width;
  const sourceHeight = swap ? source.width : source.height;

  if (Math.max(sourceWidth, sourceHeight) > MAX_INPUT_EDGE) {
    source.release();
    throw new Error(
      `${file.name}: ${sourceWidth}×${sourceHeight}px exceeds the ${MAX_INPUT_EDGE}px limit`,
    );
  }

  const maxEdge = opts.workingMaxEdge ?? 4200;
  const k = Math.min(1, maxEdge / Math.max(sourceWidth, sourceHeight));
  // Drawn size, in the decoded (pre-rotation) axis order.
  const dw = Math.max(1, Math.round(source.width * k));
  const dh = Math.max(1, Math.round(source.height * k));
  const w = swap ? dh : dw;
  const h = swap ? dw : dh;

  const canvas = makeCanvas(w, h);
  const ctx = ctx2d(canvas);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  applyOrientation(ctx, drawOrientation, dw, dh);
  ctx.drawImage(source.drawable, 0, 0, dw, dh);
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  source.release();

  return {
    canvas,
    width: w,
    height: h,
    sourceWidth,
    sourceHeight,
    wasHeic: heic,
    // Keep the camera's real EXIF value in diagnostics even when the browser's
    // fallback decoder already baked that transform into the pixels.
    orientation: exifOrientation,
  };
}

/** Apply the EXIF transform for a drawn image of `w`×`h` pixels. */
function applyOrientation(
  ctx: { transform: (a: number, b: number, c: number, d: number, e: number, f: number) => void },
  orientation: number,
  w: number,
  h: number,
) {
  switch (orientation) {
    case 2: ctx.transform(-1, 0, 0, 1, w, 0); break;
    case 3: ctx.transform(-1, 0, 0, -1, w, h); break;
    case 4: ctx.transform(1, 0, 0, -1, 0, h); break;
    case 5: ctx.transform(0, 1, 1, 0, 0, 0); break;
    case 6: ctx.transform(0, 1, -1, 0, h, 0); break;
    case 7: ctx.transform(0, -1, -1, 0, h, w); break;
    case 8: ctx.transform(0, -1, 1, 0, 0, w); break;
    default: break;
  }
}

type DecodedSource = {
  drawable: CanvasImageSource;
  width: number;
  height: number;
  /** True when the decoder already baked EXIF orientation into the pixels. */
  orientationApplied: boolean;
  release: () => void;
};

/**
 * `createImageBitmap` is the fast path and is forced to `imageOrientation:
 * "none"` so orientation handling is ours alone. The <img> fallback (older
 * iOS Safari, or a worker without createImageBitmap) applies EXIF itself.
 */
async function decodeBlob(blob: Blob): Promise<DecodedSource> {
  if (typeof createImageBitmap === "function") {
    try {
      const bitmap = await createImageBitmap(blob, { imageOrientation: "none" });
      return {
        drawable: bitmap,
        width: bitmap.width,
        height: bitmap.height,
        orientationApplied: false,
        release: () => bitmap.close?.(),
      };
    } catch {
      /* fall through to the <img> decoder */
    }
  }

  if (typeof document === "undefined" || typeof Image === "undefined") {
    throw new Error("This browser cannot decode the selected image");
  }

  const url = URL.createObjectURL(blob);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error("Could not read the selected image"));
      el.src = url;
    });
    await img.decode?.().catch(() => undefined);
    return {
      drawable: img,
      width: img.naturalWidth || img.width,
      height: img.naturalHeight || img.height,
      orientationApplied: true,
      release: () => URL.revokeObjectURL(url),
    };
  } catch (err) {
    URL.revokeObjectURL(url);
    throw err;
  }
}
